/**
 * The transaction edit/create form's state and its pure mappings: a stored
 * transaction into form fields, and the form into the create or update
 * payload (WEBF-25; the WEBF-05 "clear an emptied field" rule lives here).
 */

import type { CreateTransactionDto, Transaction, TransactionType } from '@luminaria/shared';
import type { UpdateTransactionInput } from '@shared/api/transactionsApi';

export type TransactionFormMode = 'edit' | 'create';

export interface TransactionFormState {
  accountId: string;
  action: string;
  symbol: string;
  description: string;
  quantity: string;
  price: string;
  amount: string;
  fees: string;
  tradeDate: string;
  settlementDate: string;
  securityType: string;
  excludeFromCostBasis: boolean;
  cashFlowType: '' | 'deposit' | 'withdrawal';
}

export const emptyForm: TransactionFormState = {
  accountId: '',
  action: '',
  symbol: '',
  description: '',
  quantity: '',
  price: '',
  amount: '',
  fees: '',
  tradeDate: '',
  settlementDate: '',
  securityType: '',
  excludeFromCostBasis: false,
  cashFlowType: '',
};

function toDateInputValue(value?: string | null): string {
  return value ? value.slice(0, 10) : '';
}

function toNumberInputValue(value?: number | null): string {
  return value == null ? '' : String(value);
}

function toOptionalNumber(value: string): number | undefined {
  const trimmed = value.trim();
  if (!trimmed) return undefined;
  const parsed = Number(trimmed);
  return Number.isFinite(parsed) ? parsed : undefined;
}

function toOptionalString(value: string): string | undefined {
  const trimmed = value.trim();
  return trimmed ? trimmed : undefined;
}

/** A stored transaction as edit-form fields. */
export function transactionToForm(transaction: Transaction, accountId: string): TransactionFormState {
  return {
    accountId,
    action: transaction.action,
    symbol: transaction.symbol ?? '',
    description: transaction.description ?? '',
    quantity: toNumberInputValue(transaction.quantity),
    price: toNumberInputValue(transaction.price),
    amount: toNumberInputValue(transaction.amount),
    fees: toNumberInputValue(transaction.fees),
    tradeDate: toDateInputValue(transaction.tradeDate ?? transaction.date),
    settlementDate: toDateInputValue(transaction.settlementDate),
    securityType: transaction.securityType ?? '',
    excludeFromCostBasis: transaction.excludeFromCostBasis ?? false,
    cashFlowType: transaction.cashFlowType ?? '',
  };
}

type TransactionPayloadResult =
  /** Action, amount or every date missing. */
  | { kind: 'invalid' }
  /** Create mode without an account. */
  | { kind: 'missingAccount' }
  /** Edit mode before the original transaction has loaded: nothing to send. */
  | { kind: 'notReady' }
  | { kind: 'update'; payload: UpdateTransactionInput }
  | { kind: 'create'; payload: CreateTransactionDto };

/**
 * The form as a create or update payload. The date sent is the trade date,
 * else the settlement date, else (edit) the original's date. In edit mode a
 * field the user emptied is sent as a clear (`null`), or JSON would drop the
 * key and the API keep the old value (WEBF-05); `fees` is a required column,
 * so clearing it means 0.
 */
export function toTransactionPayload(
  form: TransactionFormState,
  mode: TransactionFormMode,
  original: Transaction | undefined
): TransactionPayloadResult {
  const tradeDate = toOptionalString(form.tradeDate);
  const settlementDate = toOptionalString(form.settlementDate);
  const fallbackDate = mode === 'edit' && original
    ? toDateInputValue(original.date)
    : '';
  const effectiveDate = tradeDate ?? settlementDate ?? fallbackDate;
  const amount = toOptionalNumber(form.amount);

  if (!form.action || amount === undefined || !effectiveDate) {
    return { kind: 'invalid' };
  }

  if (mode === 'create' && !form.accountId) {
    return { kind: 'missingAccount' };
  }

  const fields = {
    symbol: toOptionalString(form.symbol),
    description: toOptionalString(form.description),
    quantity: toOptionalNumber(form.quantity),
    price: toOptionalNumber(form.price),
    fees: toOptionalNumber(form.fees),
    tradeDate,
    settlementDate,
    securityType: toOptionalString(form.securityType),
    cashFlowType: form.cashFlowType ? form.cashFlowType : undefined,
  };

  if (mode === 'edit') {
    if (!original) return { kind: 'notReady' };
    const clear = <T,>(value: T | undefined, originalValue: unknown): T | null | undefined =>
      value !== undefined ? value : originalValue != null && originalValue !== '' ? null : undefined;
    return {
      kind: 'update',
      payload: {
        action: form.action as TransactionType,
        amount,
        date: effectiveDate,
        excludeFromCostBasis: form.excludeFromCostBasis,
        symbol: clear(fields.symbol, original.symbol),
        description: clear(fields.description, original.description),
        quantity: clear(fields.quantity, original.quantity),
        price: clear(fields.price, original.price),
        fees: fields.fees ?? (original.fees ? 0 : undefined),
        tradeDate: clear(fields.tradeDate, original.tradeDate),
        settlementDate: clear(fields.settlementDate, original.settlementDate),
        securityType: clear(fields.securityType, original.securityType),
        cashFlowType: clear(fields.cashFlowType, original.cashFlowType),
      },
    };
  }

  return {
    kind: 'create',
    payload: {
      accountId: form.accountId,
      action: form.action as TransactionType,
      amount,
      date: effectiveDate,
      excludeFromCostBasis: form.excludeFromCostBasis,
      ...fields,
    },
  };
}
